import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Box,
  Avatar,
  Tooltip,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EditIcon from "@material-ui/icons/Edit";
import PersonIcon from "@material-ui/icons/Person";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { capitalizarPalabras } from "src/utils/sharedFunctions";
import { DeleteOutline } from "@material-ui/icons";
import { useSnackbar } from "src/components/snackbar";
import PortafolioTareaCardContent from "./PortafolioTareaCardContent";

const useStyles = makeStyles((theme) => ({
  card: {
    transition: "all 0.3s ease",
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      boxShadow: theme.shadows[4],
      transform: "translateY(-2px)",
    },
  },
  cardToday: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: `${theme.palette.primary.main}05`,
  },
  cardHeader: {
    paddingBottom: theme.spacing(2),
  },
  fechaContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  fechaChip: {
    fontWeight: 600,
  },
  fechaChipToday: {
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  descripcionPreview: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
  },
  metadataContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  empleadoInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  empleadoAvatar: {
    width: 32,
    height: 32,
    backgroundColor: theme.palette.grey[400],
  },
  cardActions: {
    padding: theme.spacing(1, 2),
    justifyContent: "space-between",
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  expandButton: {
    transition: "transform 0.2s ease",
  },
  expandButtonRotated: {
    transform: "rotate(180deg)",
  },
  editButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}10`,
    },
  },
  todayBadge: {
    backgroundColor: theme.palette.secondary.main,
    color: "white",
    fontSize: "0.7rem",
    height: 20,
  },
  fileCount: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    height: 24,
  },
}));

// Función para formatear fecha en español
const formatearFechaEspanol = (fecha) => {
  if (!fecha) return "";
  const [mes, anio] = fecha.split("/");
  const meses = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  return `${meses[parseInt(mes) - 1]} ${anio}`;
};

// Función para verificar si es hoy
const esHoy = (fecha) => {
  if (!fecha) return false;
  const hoy = new Date();
  const [mes, anio] = fecha.split("/");
  return (
    parseInt(mes) === hoy.getMonth() + 1 && parseInt(anio) === hoy.getFullYear()
  );
};

export default function PortafolioTareaCardList({
  portafolios,
  tarea,
  onEditarPortafolio,
  onDeletePortafolio,
  onDeleteFile,
  deletingFile,
}) {
  const classes = useStyles();
  const [expandedCard, setExpandedCard] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    portafolio: null,
  });
  const [deleting, setDeleting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleExpandClick = (portafolioId) => {
    setExpandedCard(expandedCard === portafolioId ? null : portafolioId);
  };

  const handleOpenDeleteDialog = (portafolio) => {
    setDeleteDialog({ open: true, portafolio });
  };

  const handleCloseDeleteDialog = () => {
    if (!deleting) {
      setDeleteDialog({ open: false, portafolio: null });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.portafolio) return;

    setDeleting(true);
    const result = await onDeletePortafolio(deleteDialog.portafolio);
    enqueueSnackbar("¡Entrada eliminada correctamente!", {
      variant: "success",
    });
    setDeleting(false);

    if (result.success) {
      handleCloseDeleteDialog();
    } else {
      enqueueSnackbar(result.error || "Error al eliminar la entrada", {
        variant: "error",
      });
    }
  };

  if (!portafolios || portafolios.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      {portafolios.map((portafolio, index) => {
        const { ultimaModificacion, archivosPortafolio } = portafolio;
        const esFechaHoy = esHoy(portafolio.fecha);
        const isExpanded = expandedCard === portafolio.id;
        const archivoCount = archivosPortafolio?.length || 0;

        return (
          <Grid item xs={12} key={`portafolio_${portafolio.id || index}`}>
            <Fade in={true} timeout={200 + index * 50}>
              <Card
                className={
                  esFechaHoy
                    ? `${classes.card} ${classes.cardToday}`
                    : classes.card
                }
              >
                <CardContent className={classes.cardHeader}>
                  {/* Fecha y badges */}
                  <div className={classes.fechaContainer}>
                    <CalendarTodayIcon color="action" fontSize="small" />
                    <Chip
                      label={formatearFechaEspanol(portafolio.fecha)}
                      className={
                        esFechaHoy ? classes.fechaChipToday : classes.fechaChip
                      }
                      size="small"
                    />
                    {esFechaHoy && (
                      <Chip
                        label="HOY"
                        className={classes.todayBadge}
                        size="small"
                      />
                    )}
                    {archivoCount > 0 && (
                      <Chip
                        icon={<AttachFileIcon fontSize="small" />}
                        label={`${archivoCount} archivo${
                          archivoCount !== 1 ? "s" : ""
                        }`}
                        className={classes.fileCount}
                        size="small"
                      />
                    )}
                  </div>

                  {/* Preview de descripción */}
                  <Typography
                    variant="body1"
                    className={classes.descripcionPreview}
                  >
                    {portafolio.descripcion}
                  </Typography>

                  {/* Metadata */}
                  <div className={classes.metadataContainer}>
                    {ultimaModificacion?.empleado && (
                      <div className={classes.empleadoInfo}>
                        <Avatar className={classes.empleadoAvatar}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Última modificación
                          </Typography>
                          <Typography variant="body2">
                            {ultimaModificacion.hora} -{" "}
                            {capitalizarPalabras(
                              String(ultimaModificacion.empleado),
                            )}
                          </Typography>
                        </Box>
                      </div>
                    )}
                  </div>
                </CardContent>

                <CardActions className={classes.cardActions}>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Ver detalles y archivos">
                      <IconButton
                        onClick={() => handleExpandClick(portafolio.id)}
                        className={`${classes.expandButton} ${
                          isExpanded ? classes.expandButtonRotated : ""
                        }`}
                        size="small"
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Tooltip>

                    {esFechaHoy && (
                      <>
                        <Tooltip title="Editar entrada">
                          <IconButton
                            onClick={() => onEditarPortafolio(portafolio)}
                            className={classes.editButton}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar entrada">
                          <IconButton
                            onClick={() => handleOpenDeleteDialog(portafolio)}
                            color="error"
                            size="small"
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>

                  <Typography variant="caption" color="textSecondary">
                    {isExpanded ? "Ocultar detalles" : "Ver detalles"}
                  </Typography>
                </CardActions>

                {/* Contenido expandible */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <PortafolioTareaCardContent
                    portafolio={portafolio}
                    tarea={tarea}
                    esFechaHoy={esFechaHoy}
                    onDeleteFile={onDeleteFile}
                    deletingFile={deletingFile}
                  />
                </Collapse>
              </Card>
            </Fade>
          </Grid>
        );
      })}
      {/* Diálogo de confirmación */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
      >
        <DialogTitle>¿Eliminar entrada de bitácora?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta acción eliminará permanentemente la entrada y todos sus
            archivos adjuntos. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting && <CircularProgress size={16} />}
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
